<script setup lang="ts">
import { ref } from 'vue';
import ConnxProgressSteps from '@/components/Molecules/ConnxProgressSteps.vue';
import ConnxButton from '@/components/Atoms/ConnxButton.vue';
import ConnxTfaSetupIntro from './ConnxTFASetupIntro.vue';
import ConnxTfaSetupStepOne from './ConnxTFASetupStepOne.vue';
import ConnxTfaSetupStepTwo from './ConnxTFASetupStepTwo.vue';
import ConnxTfaSetupStepThree from './ConnxTFASetupStepThree.vue';
import ConnxTfaSetupStepFour from './ConnxTFASetupStepFour.vue';
import useTFAuthenticationSettingsStore from '@/stores/tf-authentication-settings';
import type AvailableIcons from '@/interfaces/AvailableIcons';
import connxLogo from '@/assets/connx.svg';
import Steps from '@/interfaces/Steps';

const authenticationSettingsStore = useTFAuthenticationSettingsStore();
const step3 = ref();
const currentStep = ref(0);
const completed = ref(false);
const steps : Steps[] = [
  {
    title: 'Get App',
  },
  {
    title: 'ConnX Mobile',
  },
  {
    title: 'Enter Authentication',
  },
  {
    title: 'Protected',
    icon: 'fas fa-check',
  },
];

const setCompleted = () => {
  currentStep.value = steps.length;
  completed.value = true;
};

const validateAndIncrementStep = async () => {
  if (currentStep.value === 3 && authenticationSettingsStore.secretKey !== null) {
    const validated = await authenticationSettingsStore.verifyQRCode();
    if (!validated) {
      step3.value.showError();
      return;
    }
    setCompleted();
  }
  if (currentStep.value < steps.length) {
    currentStep.value += 1;
  }
};

const decrementStep = () => {
  authenticationSettingsStore.updateSecretKey(null);
  if (currentStep.value > 0) {
    currentStep.value -= 1;
  }
};

authenticationSettingsStore.initStore();
const completedLink = 'frm2faVerifyorSkip.aspx?isVerified=Y&isSkip=N';
const skipLink = 'frm2faVerifyorSkip.aspx?isVerified=N&isSkip=Y';

</script>

<template>
  <div class="tw-bg-connx-grey-tint-90">
    <div class="tw-pt-20 tw-pb-44 tw-w-full tw-flex tw-flex-col tw-min-h-screen">
      <component
        :is="connxLogo"
        class="tw-fill-current tw-w-60 tw-mx-auto"
      />
      <div
        class="
          tw-mt-10
          tw-bg-connx-white
          tw-max-w-7xl
          tw-mx-auto
          tw-border
          tw-border-solid
          tw-text-connx-grey-shade-20
          tw-flex-1
          tw-w-full
          tw-h-10/12
          tw-shadow-sm
          tw-flex
        "
      >
        <div class="tw-grow tw-flex tw-flex-col tw-justify-between">
          <header class="tw-border-solid tw-border-b tw-border-connx-grey-tint-80 tw-p-4">
            <h1 class="tw-text-2xl tw-font-semibold">
              Two Factor Authentication Setup
            </h1>
          </header>
          <main
            class="
              tw-mx-auto
              tw-w-full
              tw-max-w-5xl
              tw-h-full
              tw-flex
              tw-flex-col
              tw-px-6
              tw-py-10
              tw-justify-start
            "
          >
            <connx-progress-steps
              :current-step="currentStep"
              :steps="steps"
              class="tw-mb-20"
            />
            <div class="tw-px-10">
              <connx-tfa-setup-intro v-if="currentStep === 0" />
              <connx-tfa-setup-step-one
                v-if="currentStep === 1"
                :content="authenticationSettingsStore.settings.instructionsForDesktop"
              />
              <connx-tfa-setup-step-two v-if="currentStep === 2" />
              <connx-tfa-setup-step-three
                v-if="currentStep === 3"
                ref="step3"
                @complete="validateAndIncrementStep()"
              />
              <connx-tfa-setup-step-four v-if="currentStep === 4 && completed" />
            </div>
          </main>
          <footer class="tw-w-10/12 tw-px-4 tw-mx-auto tw-border-solid tw-border-t tw-border-connx-grey-tint-80 tw-py-6">
            <div
              v-if="completed"
              class="tw-text-center"
            >
              <connx-button :link="completedLink">
                Finish
              </connx-button>
            </div>
            <div
              v-else
              class="tw-flex tw-justify-between tw-px-4 sm:tw-px-0"
            >
              <div
                v-if="authenticationSettingsStore.gracePeriod.canSkip2faSetup"
                v-show="!completed"
                class="tw-flex tw-flex-wrap sm:tw-space-y-0 tw-items-center tw-justify-between"
              >
                <connx-button
                  :link="skipLink"
                  aria-label="Skip"
                  class="tw-mr-4 md:tw-w-36"
                >
                  Skip
                </connx-button>
                <p>
                  You have {{ authenticationSettingsStore.gracePeriod.daysToSkip2faSetup }} days left to skip
                </p>
              </div>
              <div v-else></div>
              <div>
                <div class="tw-flex tw-justify-between tw-flex-wrap">
                  <connx-button
                    v-if="currentStep > 1"
                    icon="fas fa-arrow-left"
                    icon-position="before"
                    aria-label="Previous"
                    class="tw-w-full md:tw-w-36 md:tw-mr-4 tw-mb-4 md:tw-mb-0"
                    @click="decrementStep()"
                  >
                    Previous
                  </connx-button>
                  <connx-button
                    v-if="currentStep !== 3"
                    icon="fas fa-arrow-right"
                    class="tw-w-full md:tw-w-36"
                    button-style="primary"
                    aria-label="Next"
                    @click="validateAndIncrementStep()"
                  >
                    Next
                  </connx-button>
                  <connx-button
                    v-else
                    icon="far fa-shield-check"
                    aria-label="Verify"
                    button-style="success"
                    icon-position="before"
                    class="tw-w-full md:tw-w-36"
                    :disabled="authenticationSettingsStore.secretKey === null"
                    @click="validateAndIncrementStep()"
                  >
                    Verify
                  </connx-button>
                </div>
              </div>
            </div>
          </footer>
        </div>
      </div>
    </div>
  </div>
</template>
